import PropTypes from 'prop-types';
import { equals, find, path, propEq } from 'ramda';
import React, { Component } from 'react';
import { Grid, Row, Col } from 'react-flexbox-grid';

import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import { connect } from 'react-redux';
import { readEndpoint, updateResource } from 'redux-json-api';
import { SubmissionError } from 'redux-form';

import { closeLeftBarModule, showSnackbarMessage } from '../../modules/actions/layout';
import CompanyForm from './Form';

class CompanyEdit extends Component {
  static propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    readEndpoint: PropTypes.func.isRequired,
    updateResource: PropTypes.func.isRequired,
    closeLeftBarModule: PropTypes.func.isRequired,
    showSnackbarMessage: PropTypes.func.isRequired,
    extra: PropTypes.shape({}),
  };

  componentWillMount() {
    const { readEndpoint, extra: { id } } = this.props;

    readEndpoint(`company/${id}`);
  };

  render() {
    const { company, apiEndpoint, updateResource, closeLeftBarModule, showSnackbarMessage } = this.props;
    const initialValues = {
      name: path(['attributes', 'name'], company),
      address: path(['attributes', 'address'], company),
      zipcode: path(['attributes', 'zipcode'], company),
      city: path(['attributes', 'city'], company),
      country: path(['attributes', 'country'], company),
      phone: path(['attributes', 'phone'], company),
      phone_24h: path(['attributes', 'phone-24h'], company),
      phone_24h_2: path(['attributes', 'phone-24h-2'], company),
      email: path(['attributes', 'email'], company),
      website: path(['attributes', 'website'], company),
      bank_account: path(['attributes', 'bank-account'], company),
      kvk_number: path(['attributes', 'kvk-number'], company),
      is_active: path(['attributes', 'is-active'], company),
      serviced_railways: path(['relationships', 'serviced-railways', 'data'], company).map(r => r.id) || [],
      active_subcontractor: path(['relationships', 'active-subcontractor', 'data', 'id'], company) || null,
    };

    return (
      <Grid fluid>
        <Row>
          <Col style={{ margin: 20, width: '100%' }}>
            <CardHeader title="Bedrijf wijzigen" />

            <Divider />

            <CompanyForm
              companyId={String(company.id)}
              initialValues={initialValues}
              onSubmit={async (values) => {
                try {
                  const entity = {
                    type: 'company',
                    id: company.id,
                    attributes: {
                      'name': values.name,
                      'address': values.address,
                      'zipcode': values.zipcode,
                      'city': values.city,
                      'country': values.country,
                      'phone': values.phone,
                      'phone-24h': values.phone_24h,
                      'email': values.email,
                      'website': values.website,
                      'bank-account': values.bank_account,
                      'kvk-number': values.kvk_number,
                    },
                  };

                  if (values.phone_24h_2) entity['phone-24h-2'] = values.phone_24h_2;

                  if (!equals(initialValues.active_subcontractor, values.active_subcontractor))
                    entity.attributes['active-subcontractor'] = `${apiEndpoint}/api-user/${values.active_subcontractor}`;

                  if (values.serviced_railways &&
                      !equals(initialValues.serviced_railways, values.serviced_railways))
                    entity.attributes['serviced-railways'] = values.serviced_railways.map(r => `${apiEndpoint}/railway/${r}`);

                  // If the company's active state has changed, update it.
                  if (typeof values.is_active === 'boolean' &&
                      values.is_active !== path(['attributes', 'is-active'], company))
                    entity.attributes['is-active'] = values.is_active;

                  await updateResource(entity);
                  closeLeftBarModule('company');
                  showSnackbarMessage('Bedrijf gewijzigd');
                } catch (exc) {
                  let errors = {};
                  const res = await exc.response.json();

                  res.errors.forEach(e => {
                    try {
                      const field = path(['source', 'pointer'], e)
                        .replace('/data/attributes/', '')
                        .replace('-', '_');

                      if (field === 'non_field_errors') errors._error = e.detail;
                      else {
                        errors[field] = e.detail;

                        switch (field) {
                          case 'name': {
                            errors[field] += ' (voorbeeld: Arriva Touring)';
                            break;
                          }
                          case 'address': {
                            errors[field] += ' (voorbeeld: Bornholmstraat 60)';
                            break;
                          }
                          case 'zipcode': {
                            errors[field] += ' (voorbeeld: 9723 AZ)';
                            break;
                          }
                          case 'city': {
                            errors[field] += ' (voorbeeld: Groningen)';
                            break;
                          }
                          case 'country': {
                            errors[field] += ' (kies een land uit de lijst)';
                            break;
                          }
                          case 'phone': {
                            errors[field] += ' (voorbeeld: 050 526 0268)';
                            break;
                          }
                          case 'phone_24h': {
                            errors[field] += ' (voorbeeld: 06 12345678)';
                            break;
                          }
                          case 'phone_24h_2': {
                            errors[field] += ' (voorbeeld: 06 12345678)';
                            break;
                          }
                          case 'email': {
                            errors[field] += ' (voorbeeld: info@arrivatouring.nl)';
                            break;
                          }
                          case 'website': {
                            errors[field] += ' (voorbeeld: https://www.arrivatouring.nl)';
                            break;
                          }
                          case 'bank_account': {
                            errors[field] += ' (voorbeeld: NL54RABO0383008336)';
                            break;
                          }
                          case 'kvk_number': {
                            errors[field] += ' (voorbeeld: 12345678)';
                            break;
                          }
                        }
                      }
                    } catch (e2) {
                      /**
                       * Special error logging for cases where the errors from
                       * the API are of an unknown format.
                       */
                      console.group('CompanyEdit');
                      console.error('uncaught error while parsing response errors: ' + e2);
                      console.error('original error: ' + JSON.stringify(e, null, 4));
                      console.groupEnd();
                    }
                  });

                  showSnackbarMessage('Er is iets fout gegaan bij het wijzigen van het bedrijf.');

                  throw new SubmissionError(errors);
                }
              }}
              handleDelete={async () => {
                try {
                  const entity = {
                    type: 'company',
                    id: company.id,
                    attributes: {
                      deleted_at: new Date()
                    },
                  }

                  await updateResource(entity);
                  closeLeftBarModule('company');
                  showSnackbarMessage('Bedrijf verwijderd');
                } catch (exc) {
                  showSnackbarMessage('Er is iets misgegaan bij het verwijderen van het bedrijf.');

                  throw new SubmissionError(exc);
                }
              }}
            />
          </Col>
        </Row>
      </Grid>
    );
  };
};

function selectCompany(state, id) {
  const companies = state.api.company && state.api.company.data || [];

  return find(propEq('id', id))(companies);
};

const mapStateToProps = (state, ownProps) => ({
  apiEndpoint: path(['api', 'endpoint', 'axiosConfig', 'baseURL'], state),
  company: selectCompany(state, ownProps.extra.id),
});

const mapDispatchToProps = {
  readEndpoint,
  updateResource,
  closeLeftBarModule,
  showSnackbarMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyEdit);
